import React from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, BarChart3, Calendar, Settings, Zap } from 'lucide-react';
import { useTasks } from '../context/TaskContext';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/calendar', icon: Calendar, label: 'Calendar' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const Sidebar = () => {
  const { tasks, alertTasks } = useTasks();
  const pending = tasks.filter(t => t.status !== 'Completed').length;
  const completed = tasks.filter(t => t.status === 'Completed').length;
  const progress = tasks.length ? Math.round((completed / tasks.length) * 100) : 0;

  return (
    <aside className="w-14 sm:w-56 flex-shrink-0 bg-[#0f1117] dark:bg-[#0b0e1a] border-r border-gray-800 flex flex-col h-full">
      {/* Logo */}
      <div className="px-3 sm:px-4 py-5 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Zap size={16} className="text-white" fill="white" />
          </div>
          <div className="hidden sm:block">
            <p className="text-sm font-bold text-white leading-none">Flow</p>
            <p className="text-[10px] text-gray-500 mt-0.5">Productivity</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto scrollbar-thin">
        <p className="hidden sm:block text-[10px] font-semibold text-gray-600 uppercase tracking-widest px-2 pb-2">Menu</p>
        {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm transition-all group ${
                isActive
                  ? 'bg-indigo-500 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={17} className="flex-shrink-0" />
                <span className="hidden sm:block font-medium">{label}</span>
                {label === 'Dashboard' && alertTasks.length > 0 && (
                  <span className={`hidden sm:flex ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-red-500 text-white'}`}>
                    {alertTasks.length}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom stats */}
      <div className="hidden sm:block p-4 border-t border-gray-800">
        <div className="mb-2 flex justify-between items-center">
          <span className="text-[11px] text-gray-500">Progress</span>
          <span className="text-[11px] font-semibold text-indigo-400">{progress}%</span>
        </div>
        <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-emerald-400 rounded-full transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-3 flex gap-3">
          <div className="flex-1 text-center">
            <p className="text-base font-bold text-white">{pending}</p>
            <p className="text-[10px] text-gray-500">Pending</p>
          </div>
          <div className="flex-1 text-center">
            <p className="text-base font-bold text-emerald-400">{completed}</p>
            <p className="text-[10px] text-gray-500">Done</p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
